import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import ViewPicker from './ViewPicker';
import { IdentityChallengeContext } from './contextDoNotImport';

const EARLY_FLOW_EXIT = 'IDENTITY_CHALLENGE_FLOW_CLOSED';

// Actions
const START_CHALLENGE_FLOW = 'start-challenge-flow';
const CLOSE_CHALLENGE_FLOW = 'close-challenge-flow';
const SET_CHALLENGE_FLOW_COLLECTION = 'set-challenge-flow-collection';
const CHALLENGE_FLOW_REFRESH_STARTED = 'challenge-flow-started';
const CHALLENGE_FLOW_REFRESH_SUCCEEDED = 'challenge-flow-succeeded';
const CHALLENGE_FLOW_REFRESH_FAILED = 'challenge-flow-refresh-failed';

const SET_ATTEMPT_TO_CLOSE_REMEDIATION = 'set-attempt-to-close-remediation';

const isDicrs = (coll) => {
  const firstResource = coll?.resources().head();
  const metaTypeParts = firstResource?.type()?.split('-') || [];
  const metaTypePrefix = metaTypeParts[0];

  return metaTypePrefix === 'dicrs';
};

const init = () => {
  return {
    isAttemptingToCloseRemediation: false,
    viewType: null,
    challengeFlowCollection: null,
    challengeFlowCollectionLoading: false,
    challengeFlowCollectionRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case CLOSE_CHALLENGE_FLOW: {
      return init();
    }
    case START_CHALLENGE_FLOW: {
      return {
        ...state,
        viewType: 'dialog', // action.view
        challengeFlowCollection: action.challengeFlowCollection,
      };
    }
    case SET_CHALLENGE_FLOW_COLLECTION: {
      return {
        ...state,
        challengeFlowCollection: action.challengeFlowCollection,
      };
    }
    case CHALLENGE_FLOW_REFRESH_STARTED: {
      return {
        ...state,
        challengeFlowCollectionLoading: true,
        challengeFlowCollectionRefreshError: null,
      };
    }
    case CHALLENGE_FLOW_REFRESH_SUCCEEDED: {
      return {
        ...state,
        challengeFlowCollectionLoading: false,
        challengeFlowCollectionRefreshError: null,
        challengeFlowCollection: action.challengeFlowCollection,
      };
    }
    case CHALLENGE_FLOW_REFRESH_FAILED: {
      return {
        ...state,
        challengeFlowCollectionLoading: false,
        challengeFlowCollectionRefreshError: action.challengeFlowCollectionRefreshError,
      };
    }
    case SET_ATTEMPT_TO_CLOSE_REMEDIATION: {
      return {
        ...state,
        isAttemptingToCloseRemediation: action.isAttemptingToCloseRemediation,
      };
    }
    default: {
      throw new Error(`Unhandled IdentityChallengeContext action type: ${action.type}`);
    }
  }
};

const IdentityChallengeProvider = ({
  children,
}) => {
  const promiseRef = React.useRef(null);
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const setChallengeFlowCollection = React.useCallback(async (coll) => {
    if (isDicrs(coll)) {
      dispatch({ type: SET_CHALLENGE_FLOW_COLLECTION, challengeFlowCollection: coll });
    } else {
      promiseRef.current.resolve(coll);
    }
  }, []);

  const handleSuccess = React.useCallback(async () => {
    const {
      challengeFlowCollection,
    } = state;

    const [resource] = challengeFlowCollection;

    if (resource.relationship(fr.relationships.protectedResponse.name)) {
      const collection = await resource.relationship(fr.relationships.protectedResponse.name).getRelated();
      promiseRef.current.resolve(collection);
    } else {
      throw new Error('Developer configuration error: A success resource must have a protected response relationship');
    }
  }, [state]);

  const refreshChallengeFlowCollection = React.useCallback(async () => {
    const { challengeFlowCollection: challengeFlowColl } = state;

    if (challengeFlowColl === null) {
      return;
    }

    if (challengeFlowColl.resources().length() === 0) {
      return;
    }

    const [challengeFlowRes] = challengeFlowColl;

    try {
      dispatch({ type: CHALLENGE_FLOW_REFRESH_STARTED });

      const coll = await challengeFlowRes.link('self').get();

      dispatch({ type: CHALLENGE_FLOW_REFRESH_SUCCEEDED, challengeFlowCollection: coll });
    } catch (err) {
      dispatch({ type: CHALLENGE_FLOW_REFRESH_FAILED, challengeFlowCollectionRefreshError: err });
    }
  }, [state]);

  const handleInterceptResponse = React.useCallback(async (coll, onSuccessfulChallenge, onFailedChallenge, args = {}) => {
    const { onEarlyClose = () => {} } = args;

    if (!isDicrs(coll)) {
      return onSuccessfulChallenge(coll);
    }

    // Start the flow
    dispatch({ type: START_CHALLENGE_FLOW, challengeFlowCollection: coll });

    const deferredPromise = new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });

    try {
      // only ever resolve the promise if we get back the original request
      const res = await deferredPromise;
      dispatch({ type: CLOSE_CHALLENGE_FLOW });
      return onSuccessfulChallenge(res);
    } catch (err) {
      if (err === EARLY_FLOW_EXIT) {
        return onEarlyClose();
      }

      return onFailedChallenge(err);
    }
  }, []);

  const handleClose = React.useCallback(() => {
    promiseRef.current.reject(EARLY_FLOW_EXIT);
    dispatch({ type: CLOSE_CHALLENGE_FLOW });
  }, [state]);

  const handleConfirmClose = React.useCallback(async () => {
    const { challengeFlowCollection } = state;
    const [challengeFlowResource] = challengeFlowCollection;

    if (challengeFlowResource.relationship(fr.relationships.mfaChallengeCancelConfirmation.name)) {
      try {
        const coll = await challengeFlowResource.relationship(fr.relationships.mfaChallengeCancelConfirmation.name).link('related').get();
        await setChallengeFlowCollection(coll);
      } catch (err) {
        dispatch({ type: CHALLENGE_FLOW_REFRESH_FAILED, challengeFlowCollectionRefreshError: err });
        // FW-14644 - if an error is encountered make sure to close the modal rather than risk it lingering
        handleClose();
      }
    } else {
      handleClose();
    }
  }, [state, handleClose, setChallengeFlowCollection]);

  const handleAttemptToCloseRemediation = React.useCallback((isClosing) => {
    dispatch({ type: SET_ATTEMPT_TO_CLOSE_REMEDIATION, isAttemptingToCloseRemediation: isClosing });
  }, []);

  const memoizedValue = React.useMemo(() => {
    let challengeFlowResource = null;

    if (state.challengeFlowCollection) {
      if (state.challengeFlowCollection.resources().length() !== 1) {
        throw new Error('Challenge flow collection should have exactly 1 resource');
      }
      challengeFlowResource = state.challengeFlowCollection.resources().head();
    }

    return {
      viewType: state.viewType,
      challengeFlowCollection: state.challengeFlowCollection,
      challengeFlowCollectionLoading: state.challengeFlowCollectionLoading,
      challengeFlowCollectionRefreshError: state.challengeFlowCollectionRefreshError,
      isAttemptingToCloseRemediation: state.isAttemptingToCloseRemediation,
      handleClose,
      handleConfirmClose,
      handleSuccess,
      challengeFlowResource,
      setChallengeFlowCollection,
      refreshChallengeFlowCollection,
      handleInterceptResponse,
      handleAttemptToCloseRemediation,
    };
  }, [
    state,
    handleSuccess,
    handleClose,
    handleConfirmClose,
    setChallengeFlowCollection,
    refreshChallengeFlowCollection,
    handleInterceptResponse,
    handleAttemptToCloseRemediation,
  ]);

  return (
    <IdentityChallengeContext.Provider value={memoizedValue}>
      {children}
      <ViewPicker />
    </IdentityChallengeContext.Provider>
  );
};

// Use IdentityChallenge State Context
const useIdentityChallengeContext = () => {
  const context = React.useContext(IdentityChallengeContext);
  if (context === undefined) {
    throw new Error('useIdentityChallengeContext must be used within a IdentityChallengeProvider');
  }
  return context;
};

IdentityChallengeProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  IdentityChallengeProvider,
  useIdentityChallengeContext,
};
